/**
 * Snapshot WebRTC (getStats) para diagnóstico de quedas.
 *
 * O RealtimeKit usa mediasoup por baixo e NÃO expõe as peer connections
 * na API pública — elas ficam escondidas dentro dos transports (send/recv).
 * Buscar no grafo do meeting client (BFS) falha ("no_peer_connection").
 *
 * Solução: interceptamos `RTCPeerConnection` na CRIAÇÃO (registro global),
 * técnica padrão para SDKs que escondem o PC. Assim lemos getStats() das
 * conexões reais. Em queda, o PC pode já estar fechado — por isso o Room
 * também guarda o último snapshot periódico.
 */

// ---------------------------------------------------------------------------
// Registro global de RTCPeerConnection (intercepta na criação).
// ---------------------------------------------------------------------------

const trackedPeerConnections = new Set<RTCPeerConnection>();
let peerConnectionTrackerInstalled = false;

/**
 * Substitui window.RTCPeerConnection por um wrapper que registra cada
 * conexão criada. DEVE rodar antes do SDK criar os transports (importar
 * este módulo cedo já garante isso). Idempotente e à prova de falha.
 */
export function installPeerConnectionTracker(): void {
    if (peerConnectionTrackerInstalled || typeof window === "undefined") {
        return;
    }
    const NativePc = window.RTCPeerConnection;
    if (typeof NativePc !== "function") {
        return;
    }
    peerConnectionTrackerInstalled = true;

    const Wrapped = function (
        this: unknown,
        ...args: [RTCConfiguration?]
    ): RTCPeerConnection {
        const pc = new NativePc(...args);
        try {
            trackedPeerConnections.add(pc);
            const cleanup = () => {
                if (
                    pc.connectionState === "closed" ||
                    pc.iceConnectionState === "closed"
                ) {
                    trackedPeerConnections.delete(pc);
                }
            };
            pc.addEventListener("connectionstatechange", cleanup);
            pc.addEventListener("iceconnectionstatechange", cleanup);
        } catch {
            // registro é reforço — nunca quebra a criação do PC
        }
        return pc;
    } as unknown as typeof RTCPeerConnection;

    // Preserva prototype (instanceof) e métodos estáticos (generateCertificate).
    Wrapped.prototype = NativePc.prototype;
    try {
        Object.setPrototypeOf(Wrapped, NativePc);
    } catch {
        // ignora ambientes que travam setPrototypeOf
    }

    try {
        window.RTCPeerConnection = Wrapped;
        const w = window as unknown as {
            webkitRTCPeerConnection?: typeof RTCPeerConnection;
        };
        if (typeof w.webkitRTCPeerConnection === "function") {
            w.webkitRTCPeerConnection = Wrapped;
        }
    } catch {
        peerConnectionTrackerInstalled = false;
    }
}

/** Conexões vivas registradas (remove as fechadas ao consultar). */
export function getTrackedPeerConnections(): RTCPeerConnection[] {
    for (const pc of Array.from(trackedPeerConnections)) {
        if (
            pc.connectionState === "closed" ||
            pc.iceConnectionState === "closed"
        ) {
            trackedPeerConnections.delete(pc);
        }
    }
    return Array.from(trackedPeerConnections);
}

export interface IceCandidateSummary {
    type: string | null;
    protocol: string | null;
    address: string | null;
    port: number | null;
    relayProtocol: string | null;
}

export interface CandidatePairSummary {
    state: string | null;
    nominated: boolean | null;
    local: IceCandidateSummary | null;
    remote: IceCandidateSummary | null;
    /** host | srflx | relay | prflx | … */
    localCandidateType: string | null;
    remoteCandidateType: string | null;
    /** udp | tcp | … */
    protocol: string | null;
    /** Indica uso de TURN (local ou remote relay). */
    usingRelay: boolean;
    currentRoundTripTimeMs: number | null;
    availableOutgoingBitrate: number | null;
}

export interface WebRtcSnapshot {
    collectedAt: number;
    peerConnectionCount: number;
    /** Packet loss agregado inbound (0–100). */
    packetLossPercent: number | null;
    packetsReceived: number | null;
    packetsLost: number | null;
    /** Jitter médio inbound em ms. */
    jitterMs: number | null;
    /** RTT do candidate-pair selecionado (ms). */
    rttMs: number | null;
    /**
     * Caminho ICE resumido, ex.: "relay/udp", "srflx/udp", "host/tcp".
     * Preferência: tipo do candidate local (o que o cliente usou para sair).
     */
    icePath: string | null;
    /** bits/s */
    bitrateOutboundBps: number | null;
    bitrateInboundBps: number | null;
    outboundWidth: number | null;
    outboundHeight: number | null;
    inboundWidth: number | null;
    inboundHeight: number | null;
    /** frames/s enviados (vídeo). */
    outboundFrameRate: number | null;
    /** frames/s recebidos (vídeo). */
    inboundFrameRate: number | null;
    /** RTCPeerConnection.iceConnectionState */
    iceConnectionState: string | null;
    /** RTCPeerConnection.connectionState */
    connectionState: string | null;
    /** RTCPeerConnection.iceGatheringState */
    iceGatheringState: string | null;
    /** Participantes na sala (self + joined). */
    participantCount: number | null;
    /** Streams de áudio/vídeo ativos (self + remotos). */
    streamCount: number | null;
    localAudioEnabled: boolean | null;
    localVideoEnabled: boolean | null;
    candidatePair: CandidatePairSummary | null;
    error: string | null;
}

type StatsLike = Record<string, unknown>;

function asNumber(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asString(value: unknown): string | null {
    return typeof value === "string" && value.length > 0 ? value : null;
}

function asBool(value: unknown): boolean | null {
    return typeof value === "boolean" ? value : null;
}

function isPeerConnection(value: unknown): value is RTCPeerConnection {
    return (
        typeof RTCPeerConnection !== "undefined" &&
        value instanceof RTCPeerConnection
    );
}

/** Encontra RTCPeerConnection no objeto do meeting (SDK não documenta o caminho). */
export function findPeerConnections(
    root: unknown,
    maxNodes = 400,
): RTCPeerConnection[] {
    if (!root || typeof root !== "object") {
        return [];
    }

    const found: RTCPeerConnection[] = [];
    const seen = new Set<unknown>();
    const queue: unknown[] = [root];

    while (queue.length > 0 && seen.size < maxNodes) {
        const node = queue.shift();
        if (!node || typeof node !== "object") {
            continue;
        }
        if (seen.has(node)) {
            continue;
        }
        seen.add(node);

        if (isPeerConnection(node)) {
            found.push(node);
            continue;
        }

        try {
            const values = Array.isArray(node)
                ? node
                : Object.values(node as Record<string, unknown>);
            for (const child of values) {
                if (child && typeof child === "object") {
                    queue.push(child);
                }
            }
        } catch {
            // propriedades com getter que lançam — ignora
        }
    }

    return found;
}

function candidateSummary(
    stats: Map<string, StatsLike>,
    candidateId: string | null,
): IceCandidateSummary | null {
    if (!candidateId) {
        return null;
    }
    const c = stats.get(candidateId);
    if (!c) {
        return null;
    }
    return {
        type: asString(c.candidateType) ?? asString(c.type),
        protocol: asString(c.protocol),
        address: asString(c.address) ?? asString(c.ip) ?? asString(c.ipAddress),
        port: asNumber(c.port),
        relayProtocol: asString(c.relayProtocol),
    };
}

function pickSelectedPair(stats: Map<string, StatsLike>): StatsLike | null {
    const pairs = Array.from(stats.values()).filter(
        (s) => asString(s.type) === "candidate-pair",
    );
    if (pairs.length === 0) {
        return null;
    }

    const selected = pairs.find(
        (p) => p.selected === true || asString(p.state) === "succeeded",
    );
    if (selected) {
        const nominated = pairs.filter(
            (p) =>
                (p.selected === true || asString(p.state) === "succeeded") &&
                p.nominated === true,
        );
        return nominated[0] ?? selected;
    }

    return pairs[0] ?? null;
}

interface BytesSample {
    at: number;
    inboundBytes: number;
    outboundBytes: number;
}

let lastBytesSample: BytesSample | null = null;

function bitrateFromDelta(
    currentBytes: number,
    previousBytes: number,
    elapsedMs: number,
): number | null {
    if (elapsedMs <= 0 || currentBytes < previousBytes) {
        return null;
    }
    return Math.round(((currentBytes - previousBytes) * 8 * 1000) / elapsedMs);
}

/** Converte RTCStatsReport (ou mapa de teste) no snapshot de diagnóstico. */
export function parseWebRtcStatsReport(
    report: RTCStatsReport | Map<string, StatsLike> | StatsLike[],
    peerConnectionCount: number,
    collectedAt = Date.now(),
    options?: {
        updateByteSample?: boolean;
        iceConnectionState?: string | null;
        connectionState?: string | null;
        iceGatheringState?: string | null;
        participantCount?: number | null;
        streamCount?: number | null;
        localAudioEnabled?: boolean | null;
        localVideoEnabled?: boolean | null;
    },
): WebRtcSnapshot {
    const stats = new Map<string, StatsLike>();

    if (Array.isArray(report)) {
        report.forEach((s, i) => {
            const id = asString(s.id) ?? `row-${i}`;
            stats.set(id, s);
        });
    } else if (report instanceof Map) {
        report.forEach((value, key) => stats.set(key, value as StatsLike));
    } else {
        report.forEach((value) => {
            const row = value as StatsLike;
            const id = asString(row.id);
            if (id) {
                stats.set(id, row);
            }
        });
    }

    let packetsReceived = 0;
    let packetsLost = 0;
    let jitterSum = 0;
    let jitterSamples = 0;
    let inboundBytes = 0;
    let outboundBytes = 0;
    let inboundWidth: number | null = null;
    let inboundHeight: number | null = null;
    let outboundWidth: number | null = null;
    let outboundHeight: number | null = null;
    let inboundBitrate: number | null = null;
    let outboundBitrate: number | null = null;
    let inboundFrameRate: number | null = null;
    let outboundFrameRate: number | null = null;

    stats.forEach((s) => {
        const type = asString(s.type);
        if (type === "inbound-rtp") {
            packetsReceived += asNumber(s.packetsReceived) ?? 0;
            packetsLost += asNumber(s.packetsLost) ?? 0;
            const jitter = asNumber(s.jitter);
            if (jitter != null) {
                jitterSum += jitter;
                jitterSamples += 1;
            }
            inboundBytes += asNumber(s.bytesReceived) ?? 0;
            const w = asNumber(s.frameWidth);
            const h = asNumber(s.frameHeight);
            if (w != null && h != null && (inboundWidth == null || w * h > inboundWidth * (inboundHeight ?? 0))) {
                inboundWidth = w;
                inboundHeight = h;
            }
            const fps = asNumber(s.framesPerSecond);
            if (fps != null) {
                inboundFrameRate = Math.max(inboundFrameRate ?? 0, fps);
            }
        }
        if (type === "outbound-rtp") {
            outboundBytes += asNumber(s.bytesSent) ?? 0;
            const w = asNumber(s.frameWidth);
            const h = asNumber(s.frameHeight);
            if (w != null && h != null && (outboundWidth == null || w * h > outboundWidth * (outboundHeight ?? 0))) {
                outboundWidth = w;
                outboundHeight = h;
            }
            const targetBitrate = asNumber(s.targetBitrate);
            if (targetBitrate != null) {
                outboundBitrate = (outboundBitrate ?? 0) + targetBitrate;
            }
            const fps = asNumber(s.framesPerSecond);
            if (fps != null) {
                outboundFrameRate = Math.max(outboundFrameRate ?? 0, fps);
            }
        }
        if (type === "remote-inbound-rtp") {
            const jitter = asNumber(s.jitter);
            if (jitter != null) {
                jitterSum += jitter;
                jitterSamples += 1;
            }
            const rtt = asNumber(s.roundTripTime);
            if (rtt != null && inboundBitrate == null) {
                // placeholder — RTT principal vem do candidate-pair
            }
            void rtt;
        }
        if (type === "media-source" || type === "track") {
            const w = asNumber(s.width) ?? asNumber(s.frameWidth);
            const h = asNumber(s.height) ?? asNumber(s.frameHeight);
            if (w != null && h != null && outboundWidth == null) {
                outboundWidth = w;
                outboundHeight = h;
            }
        }
    });

    const pair = pickSelectedPair(stats);
    const pairRttSec = pair ? asNumber(pair.currentRoundTripTime) : null;
    const availableOut = pair ? asNumber(pair.availableOutgoingBitrate) : null;

    stats.forEach((s) => {
        if (asString(s.type) === "transport") {
            const out = asNumber(s.bytesSent);
            const inn = asNumber(s.bytesReceived);
            if (out != null) {
                outboundBytes = Math.max(outboundBytes, out);
            }
            if (inn != null) {
                inboundBytes = Math.max(inboundBytes, inn);
            }
        }
    });

    // Bitrate por delta entre amostras (precisa de 2 leituras).
    if (lastBytesSample && collectedAt > lastBytesSample.at) {
        const elapsed = collectedAt - lastBytesSample.at;
        const outBps = bitrateFromDelta(
            outboundBytes,
            lastBytesSample.outboundBytes,
            elapsed,
        );
        const inBps = bitrateFromDelta(
            inboundBytes,
            lastBytesSample.inboundBytes,
            elapsed,
        );
        if (outBps != null) {
            outboundBitrate = outBps;
        }
        if (inBps != null) {
            inboundBitrate = inBps;
        }
    }

    if (outboundBitrate == null && availableOut != null) {
        outboundBitrate = availableOut;
    }

    if (options?.updateByteSample !== false) {
        lastBytesSample = {
            at: collectedAt,
            inboundBytes,
            outboundBytes,
        };
    }

    const totalPackets = packetsReceived + packetsLost;
    const packetLossPercent =
        totalPackets > 0 ? Math.round((packetsLost / totalPackets) * 1000) / 10 : null;

    const jitterMs =
        jitterSamples > 0
            ? Math.round((jitterSum / jitterSamples) * 1000 * 10) / 10
            : null;

    const localId = pair ? asString(pair.localCandidateId) : null;
    const remoteId = pair ? asString(pair.remoteCandidateId) : null;
    const local = candidateSummary(stats, localId);
    const remote = candidateSummary(stats, remoteId);
    const localType = local?.type ?? null;
    const remoteType = remote?.type ?? null;
    const protocol = local?.protocol ?? remote?.protocol ?? null;
    const usingRelay =
        localType === "relay" ||
        remoteType === "relay" ||
        local?.relayProtocol != null;

    const candidatePair: CandidatePairSummary | null = pair
        ? {
              state: asString(pair.state),
              nominated: asBool(pair.nominated),
              local,
              remote,
              localCandidateType: localType,
              remoteCandidateType: remoteType,
              protocol,
              usingRelay,
              currentRoundTripTimeMs:
                  pairRttSec != null ? Math.round(pairRttSec * 1000) : null,
              availableOutgoingBitrate: availableOut,
          }
        : null;

    const rttMs = candidatePair?.currentRoundTripTimeMs ?? null;
    const icePath = formatIcePath(localType, remoteType, protocol, usingRelay);

    return {
        collectedAt,
        peerConnectionCount,
        packetLossPercent,
        packetsReceived: packetsReceived || null,
        packetsLost: packetsLost || null,
        jitterMs,
        rttMs,
        icePath,
        bitrateOutboundBps: outboundBitrate,
        bitrateInboundBps: inboundBitrate,
        outboundWidth,
        outboundHeight,
        inboundWidth,
        inboundHeight,
        outboundFrameRate,
        inboundFrameRate,
        iceConnectionState: options?.iceConnectionState ?? null,
        connectionState: options?.connectionState ?? null,
        iceGatheringState: options?.iceGatheringState ?? null,
        participantCount: options?.participantCount ?? null,
        streamCount: options?.streamCount ?? null,
        localAudioEnabled: options?.localAudioEnabled ?? null,
        localVideoEnabled: options?.localVideoEnabled ?? null,
        candidatePair,
        error: null,
    };
}

/** Ex.: "relay/udp", "srflx->host/udp", "unknown". */
export function formatIcePath(
    localType: string | null,
    remoteType: string | null,
    protocol: string | null,
    usingRelay: boolean,
): string | null {
    const local = localType || (usingRelay ? "relay" : null);
    if (!local && !remoteType) {
        return null;
    }
    const proto = protocol || "?";
    if (local && remoteType && remoteType !== local) {
        return `${local}->${remoteType}/${proto}`;
    }
    return `${local || remoteType || "unknown"}/${proto}`;
}

function emptySnapshot(
    partial: Partial<WebRtcSnapshot> & Pick<WebRtcSnapshot, "collectedAt" | "error">,
): WebRtcSnapshot {
    return {
        peerConnectionCount: 0,
        packetLossPercent: null,
        packetsReceived: null,
        packetsLost: null,
        jitterMs: null,
        rttMs: null,
        icePath: null,
        bitrateOutboundBps: null,
        bitrateInboundBps: null,
        outboundWidth: null,
        outboundHeight: null,
        inboundWidth: null,
        inboundHeight: null,
        outboundFrameRate: null,
        inboundFrameRate: null,
        iceConnectionState: null,
        connectionState: null,
        iceGatheringState: null,
        participantCount: null,
        streamCount: null,
        localAudioEnabled: null,
        localVideoEnabled: null,
        candidatePair: null,
        ...partial,
    };
}

/** Conta participantes e streams A/V ativos no meeting RTK. */
export function getMeetingStreamInfo(meetingClient: unknown): {
    participantCount: number | null;
    streamCount: number | null;
    localAudioEnabled: boolean | null;
    localVideoEnabled: boolean | null;
} {
    try {
        const meeting = meetingClient as {
            self?: { audioEnabled?: boolean; videoEnabled?: boolean };
            participants?: {
                joined?: {
                    toArray?: () => Array<{
                        audioEnabled?: boolean;
                        videoEnabled?: boolean;
                    }>;
                };
            };
        };

        const joined = meeting.participants?.joined?.toArray?.() ?? [];
        const localAudio = meeting.self?.audioEnabled ?? null;
        const localVideo = meeting.self?.videoEnabled ?? null;

        let streamCount = 0;
        if (localAudio) streamCount += 1;
        if (localVideo) streamCount += 1;
        for (const peer of joined) {
            if (peer.audioEnabled) streamCount += 1;
            if (peer.videoEnabled) streamCount += 1;
        }

        return {
            participantCount: joined.length + (meeting.self ? 1 : 0),
            streamCount,
            localAudioEnabled: localAudio,
            localVideoEnabled: localVideo,
        };
    } catch {
        return {
            participantCount: null,
            streamCount: null,
            localAudioEnabled: null,
            localVideoEnabled: null,
        };
    }
}

/** Tipos de evento tratados como queda (para relatório e campos no topo do doc). */
export const DROP_EVENT_TYPES = new Set([
    "roomLeft",
    "softReconnectFail",
    "reloadFallback",
]);

export async function collectWebRtcStats(
    meetingClient: unknown,
): Promise<WebRtcSnapshot | null> {
    const streamInfo = getMeetingStreamInfo(meetingClient);

    try {
        // Preferimos o registro global (interceptado na criação). Só caímos
        // no BFS do grafo do meeting como fallback (SDK antigo / sem tracker).
        const tracked = getTrackedPeerConnections();
        const pcs =
            tracked.length > 0 ? tracked : findPeerConnections(meetingClient);
        if (pcs.length === 0) {
            return emptySnapshot({
                collectedAt: Date.now(),
                error: "no_peer_connection",
                ...streamInfo,
            });
        }

        // Usa o PC com mais stats (geralmente o de mídia ativo).
        let best: WebRtcSnapshot | null = null;
        for (const pc of pcs) {
            try {
                if (pc.connectionState === "closed") {
                    continue;
                }
                const report = await pc.getStats();
                const snap = parseWebRtcStatsReport(report, pcs.length, Date.now(), {
                    iceConnectionState: pc.iceConnectionState ?? null,
                    connectionState: pc.connectionState ?? null,
                    iceGatheringState: pc.iceGatheringState ?? null,
                    ...streamInfo,
                });
                if (
                    !best ||
                    (snap.candidatePair && !best.candidatePair) ||
                    (snap.packetsReceived ?? 0) > (best.packetsReceived ?? 0)
                ) {
                    best = snap;
                }
            } catch {
                // PC individual falhou — tenta o próximo
            }
        }

        if (!best) {
            const closedPc = pcs[0];
            return emptySnapshot({
                collectedAt: Date.now(),
                peerConnectionCount: pcs.length,
                error: "peer_connections_closed",
                iceConnectionState: closedPc?.iceConnectionState ?? null,
                connectionState: closedPc?.connectionState ?? null,
                iceGatheringState: closedPc?.iceGatheringState ?? null,
                ...streamInfo,
            });
        }

        return best;
    } catch (err) {
        return emptySnapshot({
            collectedAt: Date.now(),
            error: err instanceof Error ? err.message : "collect_failed",
            ...streamInfo,
        });
    }
}

/** Eventos em que anexamos snapshot WebRTC (queda / recuperação). */
export const WEBRTC_DIAG_EVENT_TYPES = new Set([
    "roomLeft",
    "reconnecting",
    "reconnected",
    "softReconnectStart",
    "softReconnectSuccess",
    "softReconnectFail",
    "reloadFallback",
]);

/** Início de um ciclo de reconexão (para medir duração). */
export const RECONNECT_START_TYPES = new Set([
    "reconnecting",
    "softReconnectStart",
    "roomLeft",
]);

// Instala o rastreador assim que o módulo é importado (antes do join criar os PCs).
installPeerConnectionTracker();

/** Fim de um ciclo de reconexão. */
export const RECONNECT_END_TYPES = new Set([
    "reconnected",
    "softReconnectSuccess",
    "softReconnectFail",
    "reloadFallback",
]);
